<script setup lang="ts">
import useNotificationSequence from "@/notification-sequences/composables/useNotificationSequence";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import NotificationSequenceTimeline
    from "@/notification-sequences/components/notification-sequence-timeline/NotificationSequenceTimeline.vue";
import Icon from "@/core/components/icon/Icon.vue";
import {ref, watch} from "vue";
import {notificationSequenceApi} from "@/container";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/core/components/ui/tabs";
import NotificationSequenceTargetingDatatable
    from "@/notification-sequences/components/notification-sequence-targeting/NotificationSequenceTargetingDatatable.vue";
import NotificationSequenceInstancesDatatable
    from "@/notification-sequences/components/notification-sequence-instances/NotificationSequenceInstancesDatatable.vue";

const {sequenceId} = defineProps<{
    sequenceId: number
}>();

const {data: sequence, isLoading} = useNotificationSequence(sequenceId);

const enabled = ref<boolean | undefined>(sequence.value?.is_enabled ?? false);

watch(() => sequence.value, (newValue) => {
    enabled.value = newValue?.is_enabled;
});

/**
 * Toggle the enabled state of the notification sequence
 *
 * @param value
 */
const toggleEnabled = async (value: boolean | null) => {
    if (value === null) {
        return;
    }

    enabled.value = value;
    try {
        await notificationSequenceApi()
            .toggleEnabled(Number(sequenceId), value)
            .then((response) => {
                enabled.value = response.enabled;
            });
    } catch {
        enabled.value = sequence.value?.is_enabled;
    }
};
</script>

<template>
  <div class="flex-1 p-4">
    <BaseFlexSpinner v-if="isLoading || !sequence" />
    <div v-else class="space-y-4">
      <div class="bg-white shadow rounded-lg container mx-auto">
        <div class="p-4">
          <div class="flex items-center">
            <button class="p-2" @click="$router.back()">
              <Icon name="mdi-arrow-left" :size="20" color="primary" />
            </button>
            <h1>Liste des séquences {{ !!sequence ? `> ${sequence.name}` : '' }}</h1>
          </div>
          <p>{{ sequence?.description }}</p>
        </div>
        <div
          class="flex flex-col items-center justify-center p-4"
        >
          <v-switch
            :model-value="enabled"
            :color="'primary'"
            hide-details
            @update:model-value="toggleEnabled"
          >
            <template #prepend>
              <p class="text-lg">
                Activer la séquence SMS automatique
              </p>
            </template>
          </v-switch>
          <p>
            <strong>Gagnez du temps !</strong> En activant la séquence automatique, tous les patients dont le numéro de téléphone est
            renseigné recevront automatiquement un SMS le jour de leur achat.
          </p>
        </div>
        <NotificationSequenceTimeline class="p-4" :sequence="sequence" color="#3513e2" />
      </div>
      <div class="bg-white shadow rounded-lg p-4 container mx-auto">
        <Tabs default-value="targeting" class="w-full">
          <TabsList class="grid grid-cols-2">
            <TabsTrigger value="targeting">
              Ciblage patients
            </TabsTrigger>
            <TabsTrigger value="instances">
              Séquences patients
            </TabsTrigger>
          </TabsList>
          <TabsContent value="targeting">
            <NotificationSequenceTargetingDatatable :sequence="sequence" :color="'primary'" />
          </TabsContent>
          <TabsContent value="instances">
            <NotificationSequenceInstancesDatatable :sequence="sequence" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
